
import { NewsImpactAnalysis } from "../types";

// Helper to call your Python Backend
// The backend should handle the GoogleGenAI connection using your server-side API Key
const callBackendAi = async (context: any, prompt: string, taskType: string, image?: string) => {
  try {
    const payload: any = { context, prompt, taskType };
    if (image) {
        payload.image = image; // Pass Base64 image if available
    }

    const response = await fetch('/api/aiq/ai-service', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
    });
    
    if (!response.ok) return null;
    const data = await response.json();
    return data.text || data.result; // Assuming backend returns { text: "..." }
  } catch (e) {
    // Silently fail if AI service is not available
    // console.error("Backend AI Call Failed:", e);
    return null;
  }
};

// --- CORE FUNCTION: Pre-Deal Check (Optimized: Local Math Only) ---
export const analyzePreDealCheck = async (trade: any, marketContext?: any): Promise<{ status: 'Safe' | 'Warning' | 'Critical', message: string }> => {
    
    // 1. LOCAL MATH ENGINE (Runs instantly in browser)
    // This ensures "Fat Finger" checks work instantly (<10ms) without network latency.
    
    const tradePrice = Number(trade.price);
    const marketPrice = Number(marketContext?.marketPrice || 0);
    const quantity = Number(trade.quantity);
    const notional = tradePrice * quantity;

    // Rule A: Fat Finger (Price Deviation > 5%)
    if (marketPrice > 0 && tradePrice > 0) {
        const deviation = Math.abs((tradePrice - marketPrice) / marketPrice);
        const percentOff = (deviation * 100).toFixed(2);
        
        if (deviation > 0.05) { // > 5% Deviation
             return { 
                 status: 'Critical', 
                 message: `FAT FINGER ALERT: Price ${tradePrice} is ${percentOff}% away from market ref (${marketPrice}). Blocked.` 
             };
        }
    }

    // Rule B: Notional Limits
    if (notional > 5000000) { // $5M Limit
         return { 
             status: 'Critical', 
             message: `LIMIT BREACH: Deal size $${(notional/1000000).toFixed(2)}M exceeds Desk Limit ($5M). Approval required.` 
             };
    }
    if (notional > 1000000) { // $1M Warning
         return { 
             status: 'Warning', 
             message: `Large Trade Warning: Deal size $${(notional/1000000).toFixed(2)}M exceeds auto-booking threshold ($1M).` 
         };
    }

    // Optimization: We skip the slow backend AI call for this specific check.
    // Real-time deal capture requires instant feedback.
    return { status: 'Safe', message: "Validated by AiQ Rule Engine." };
};

// --- Other Services (Delegated to Backend for Heavy Lifting) ---

export const analyzeRiskScenario = async (scenario: string, contextData: any): Promise<string> => {
  const response = await callBackendAi(contextData, scenario, "risk_scenario");
  return response || "Simulation: Market volatility expected to increase. Monitor margin requirements.";
};

export const generateDashboardBriefing = async (metrics: any): Promise<string> => {
    const response = await callBackendAi(metrics, "Generate executive briefing", "dashboard_briefing");
    return response || "AiQ Intelligence: P&L is stable. No critical alerts detected in early trading.";
};

export const explainRegulatoryTerm = async (term: string): Promise<string> => {
    const response = await callBackendAi({ term }, "Explain term", "regulatory_term");
    return response || "Definition unavailable.";
};

export const chatWithAiQ = async (message: string, screenContext: string): Promise<string> => {
    const response = await callBackendAi({ screenContext }, message, "chat");
    return response || "I am currently offline. Please check the backend connection.";
};

export const analyzeNewsImpact = async (headline: string, portfolioContext: string): Promise<NewsImpactAnalysis> => {
  const response = await callBackendAi({ portfolioContext }, headline, "news_impact");
  
  if (response) {
      try {
          return typeof response === 'object' ? response : JSON.parse(response);
      } catch(e) { console.error(e); }
  }

  // Fallback if backend fails
  return {
      summary: "Impact Analysis Unavailable (Backend Offline).",
      affectedAssetClasses: ["General Market"],
      projectedPnlImpact: 0,
      recommendedAction: "Monitor volatility."
  };
};

export const generatePortfolioStrategy = async (assets: string[], riskProfile: string): Promise<string> => {
    const response = await callBackendAi({ assets, riskProfile }, "Optimize Portfolio", "portfolio_strategy");
    return response || "Optimization Engine currently offline.";
};

export const generateReportNarrative = async (reportData: any, templateType: string): Promise<string> => {
    const prompt = `
      You are writing a formal Financial Risk Report (${templateType}).
      Data: ${JSON.stringify(reportData)}
      
      Write the 'Executive Summary' paragraph. 
      - Mention the Total Valuation and P&L.
      - Highlight the key risk numbers (VaR, Limit Usage).
      - Keep it professional, third-person, suitable for a Chief Risk Officer.
      - Length: Approx 80 words.
    `;
    
    const response = await callBackendAi({}, prompt, "report_narrative");
    return response || "Executive Summary generation failed. Please input manually.";
};

export const generateProductPitch = async (productStruct: any): Promise<string> => {
    const prompt = `
        Write a sales email for structured product:
        ${JSON.stringify(productStruct)}
        Target: Private Bank. Tone: Exclusive.
    `;
    const response = await callBackendAi({}, prompt, "product_pitch");
    return response || "Sales pitch generation unavailable.";
};

export const draftMarginCall = async (counterparty: string, amount: number, currency: string, date: string): Promise<string> => {
    const prompt = `
        Draft a formal ISDA Margin Call Notice (email format).
        To: ${counterparty} Operations
        Date: ${date}
        
        We demand transfer of ${currency} ${amount.toLocaleString()} to cover the current exposure deficit.
        Reference the ISDA CSA agreement.
        Tone: Professional, Legal, Urgent.
    `;
    const response = await callBackendAi({}, prompt, "draft_email");
    return response || `Subject: Margin Call - ${counterparty}\n\nPlease transfer ${currency} ${amount} immediately per CSA terms.`;
};

export const analyzeMarginDrivers = async (counterparty: string, trades: any[]): Promise<string> => {
    const prompt = `
        Analyze the drivers of increased exposure to ${counterparty}.
        Recent Trades: ${JSON.stringify(trades.slice(0, 5))}
        
        Explain in 1 sentence why margin is required (e.g. "Driven by mark-to-market gains on 3 recent FX Swaps").
    `;
    const response = await callBackendAi({}, prompt, "margin_analysis");
    return response || "Exposure driven by recent trade valuation changes.";
};

export const explainRegulatoryRejection = async (errorCode: string, tradeData: any): Promise<string> => {
    const prompt = `
        You are a Regulatory Reporting Specialist (HKTR/EMIR).
        A trade report was Rejected by the regulator with Error Code: "${errorCode}".
        Trade Data: ${JSON.stringify(tradeData)}
        
        1. Explain what this error means in plain English.
        2. Suggest the specific fix required on the trade ticket.
        Keep it concise.
    `;
    const response = await callBackendAi({}, prompt, "reg_explanation");
    return response || "Error Code not found in knowledge base. Please check manual.";
};

export const optimizeFundingStrategy = async (deficits: any[], marketRates: any): Promise<string> => {
    const prompt = `
        You are a Corporate Treasurer.
        We have the following projected cash deficits (Shortfall):
        ${JSON.stringify(deficits)}
        
        Current Market Rates:
        ${JSON.stringify(marketRates)}
        
        Recommend a funding strategy. 
        - Should we use FX Swaps? Overnight Repo? 
        - Which currency is cheapest to borrow?
        - Provide specific trade instruction (e.g. "Sell EUR / Buy USD Spot, Buy EUR / Sell USD Fwd").
    `;
    const response = await callBackendAi({}, prompt, "funding_strategy");
    return response || "Funding strategy unavailable.";
};

export const analyzeBestExecution = async (quotes: any[], fairValue: number): Promise<string> => {
    const prompt = `
        You are a Best Execution AI. 
        We requested a quote (RFQ) and received these bids:
        ${JSON.stringify(quotes)}
        
        Our Internal Fair Value Model says the price should be: ${fairValue}.
        
        Recommendation:
        1. Which bank should we hit? (Consider Price bestness vs Fair Value).
        2. Are any quotes "off market"?
        3. Short rationale (e.g. "JPM is aggressive, likely has an axe to grind").
    `;
    const response = await callBackendAi({}, prompt, "best_execution");
    return response || "Best Ex recommendation unavailable.";
};

export const optimizeAlgoStrategy = async (strategyParams: any, backtestResults: any): Promise<string> => {
    const prompt = `
        You are an Algorithmic Trading Quant.
        I ran a backtest for a strategy with these parameters: ${JSON.stringify(strategyParams)}.
        Results: ${JSON.stringify(backtestResults)}.
        
        Critique the strategy.
        1. Is the Return vs Drawdown acceptable?
        2. Suggest one parameter tweak to improve performance (e.g. "Increase SMA period to filter noise").
    `;
    const response = await callBackendAi({}, prompt, "algo_optimization");
    return response || "Strategy analysis unavailable.";
};

export const analyzeReconciliationBreak = async (internalRecord: any, externalRecord: any): Promise<string> => {
    const prompt = `
        You are a Middle Office Reconciliation Analyst.
        I have a break between our Internal Ledger and the Broker Statement.
        
        Internal Record: ${JSON.stringify(internalRecord)}
        External Record: ${JSON.stringify(externalRecord)}
        
        Task:
        1. Identify the likely cause of the break (e.g., Missing Commission, Timing Diff, Wrong Price).
        2. Suggest the accounting entry to fix it.
        
        Keep it very short (1-2 sentences).
    `;
    const response = await callBackendAi({}, prompt, "recon_analysis");
    return response || "Break analysis unavailable.";
};

export const generateCreditMemo = async (counterpartyName: string, exposureData: any, newsSentiment: string): Promise<string> => {
    const prompt = `
        You are a Senior Credit Risk Officer.
        Write a Credit Memo for counterparty: "${counterpartyName}".
        
        Risk Data:
        - Current Exposure: ${exposureData.currentExposure}
        - Potential Future Exposure (PFE): ${exposureData.pfe}
        - Collateral Held: ${exposureData.collateral}
        
        Market Sentiment: ${newsSentiment}
        
        Conclusion:
        - Recommend keeping, cutting, or increasing the trading limit.
        - Justify with risk metrics.
        
        Format: Professional memo style. 3 paragraphs max.
    `;
    const response = await callBackendAi({}, prompt, "credit_memo");
    return response || "Credit Memo generation unavailable.";
};

export const optimizeWorkflow = async (tasks: any[]): Promise<string> => {
    const prompt = `
        You are a Systems Architect optimizing a Financial Batch Process.
        
        Current Workflow Tasks & Durations:
        ${JSON.stringify(tasks)}
        
        Identify the bottleneck and suggest an optimization (e.g., parallel processing, caching).
        Return 1 short paragraph.
    `;
    const response = await callBackendAi({}, prompt, "workflow_optimization");
    return response || "Optimization unavailable.";
};

export const generateAnalyticsDashboard = async (query: string, portfolioContext: any): Promise<{ type: 'bar' | 'pie' | 'line', data: any[], title: string, insight: string }> => {
    const prompt = `
        You are a Data Analyst BI Tool.
        User Query: "${query}"
        Portfolio Data: ${JSON.stringify(portfolioContext)}
        
        Task:
        1. Aggregate the data to answer the user's question.
        2. Choose the best chart type (bar, pie, or line).
        3. Provide a short insight summary.
        
        Response JSON Structure:
        {
            "type": "bar" | "pie" | "line",
            "title": "Chart Title",
            "data": [{ "name": "Label", "value": 123 }],
            "insight": "One sentence summary."
        }
    `;
    
    const response = await callBackendAi({}, prompt, "analytics_dashboard");
    
    if (response) {
        try {
            const parsed = typeof response === 'object' ? response : JSON.parse(response);
            return parsed;
        } catch(e) {
            console.error("Analytics Parse Error", e);
        }
    }
    
    // Fallback Mock
    return {
        type: 'bar',
        title: 'Portfolio Composition (Simulated)',
        data: [
            { name: 'Equities', value: 4500000 },
            { name: 'Fixed Income', value: 3200000 },
            { name: 'Cash', value: 1500000 }
        ],
        insight: "Simulated data shown due to backend unavailability."
    };
};

export const analyzeScreenerOpportunity = async (candidates: any[]): Promise<string> => {
    const prompt = `
        You are a Head Trader screening for opportunities.
        Here are the stocks that passed our algorithmic filter (e.g. RSI < 30, Vol > 20):
        ${JSON.stringify(candidates)}

        Pick the single most compelling "Trade of the Day".
        - Justify based on technicals (e.g. "Mean reversion setup") and sector context.
        - Suggest a trade structure (e.g. "Long Stock" or "Sell Put").
        
        Tone: Institutional, sharp, actionable.
    `;
    const response = await callBackendAi({}, prompt, "screener_analysis");
    return response || "No clear opportunity identified.";
};

export const analyzeBasketLiquidity = async (constituents: any[]): Promise<string> => {
    const prompt = `
        You are an Execution Algo Trader.
        We are about to execute a basket program execution (Buy $50M total).
        
        Constituents:
        ${JSON.stringify(constituents)}
        
        Task:
        1. Identify any illiquid names or high-volatility names that might cause slippage.
        2. Recommend an execution algorithm (VWAP vs TWAP vs Implementation Shortfall).
        
        Format: concise, bullet points.
    `;
    const response = await callBackendAi({}, prompt, "basket_algo");
    return response || "Algo analysis unavailable.";
};

export const extractTradeFromText = async (text: string, imageBase64?: string): Promise<any> => {
    // 1. AI Extraction Path (if connected)
    let prompt = `
        You are an intelligent OCR and Trade Booking Assistant.
        Extract key trade details from the provided source.
    `;
    
    if (text) {
        prompt += `\nTEXT CONTENT: "${text}"\n`;
    }
    if (imageBase64) {
        prompt += `\n[IMAGE DATA ATTACHED] - Please OCR the visual document.\n`;
    }
    
    prompt += `
        Return ONLY a valid JSON object with these fields:
        {
            "side": "Buy" | "Sell",
            "assetClass": "Equity" | "FX Spot" | "Rates Swap" | "Credit (CDS)",
            "symbol": "Ticker string",
            "quantity": Number,
            "price": Number,
            "counterparty": "Bank Name"
        }
        If a field is missing, guess based on context or put null.
    `;
    
    const response = await callBackendAi({}, prompt, "doc_extraction", imageBase64);
    
    if (response) {
        try {
            const jsonStr = response.match(/\{[\s\S]*\}/)?.[0] || response;
            return JSON.parse(jsonStr);
        } catch (e) {
            console.error("Extraction Parse Error", e);
        }
    }
    
    // 2. Robust Local Regex Fallback (If AI/Backend Offline)
    try {
        const normalized = text.toLowerCase();
        
        // Side Logic
        let side = 'Buy';
        if (text.match(/(?:Asset|Operation|Type)[:\s]+Sell/i) || text.match(/^Sell\s/im)) {
             side = 'Sell';
        } else if (text.match(/\bsold\b/i)) {
             side = 'Sell';
        }
        
        const qtyMatch = text.match(/(\d{1,3}(,\d{3})*(\.\d+)?)\s*(shares|units|qty)/i);
        const quantity = qtyMatch ? parseFloat(qtyMatch[1].replace(/,/g, '')) : 0;
        
        const priceMatch = text.match(/(?:price|@|rate)[:\s]*(\d{1,3}(,\d{3})*(\.\d+)?)/i);
        const price = priceMatch ? parseFloat(priceMatch[1].replace(/,/g, '')) : 0;
        
        // Smart Symbol Logic
        let symbol = 'UNKNOWN';
        const parenMatch = text.match(/\(([A-Z]{3,5})\)/);
        const sharesMatch = text.match(/shares\s+of\s+([A-Z]{3,5})\b/i);
        const labelMatch = text.match(/(?:Asset|Symbol|Security|Ticker)[:\s]+(?:Buy|Sell|Long|Short)?\s*.*?\b([A-Z]{3,5})\b/i);

        if (parenMatch) {
            symbol = parenMatch[1];
        } else if (sharesMatch) {
            symbol = sharesMatch[1].toUpperCase();
        } else if (labelMatch) {
            symbol = labelMatch[1].toUpperCase();
        } else {
            const candidates = text.match(/\b[A-Z]{3,5}\b/g) || [];
            const blacklist = [
                'TRADE', 'DATE', 'CONFIRM', 'CONFIRMATION', 'BUYER', 'SELLER', 'ASSET', 'PRICE', 'USD', 
                'EUR', 'GBP', 'JPY', 'CNH', 'HKD', 'AUD', 'CAD', 'CHF', 
                'SWAP', 'SPOT', 'ISDA', 'FUND', 'BANK', 'INC', 'LTD', 'CORP', 'PLC', 'GROUP', 
                'THE', 'AND', 'FOR', 'FROM', 'THIS', 'THAT', 'WITH', 
                'SHARES', 'UNITS', 'QTY', 'AMOUNT', 'TOTAL', 'NET', 'GROSS' 
            ];
            const validSymbol = candidates.find(word => !blacklist.includes(word));
            if (validSymbol) {
                symbol = validSymbol;
            }
        }

        let assetClass = 'Equity';
        if (normalized.includes('eur') || normalized.includes('jpy') || normalized.includes('fx')) assetClass = 'FX Spot';
        if (normalized.includes('swap') || normalized.includes('libor')) assetClass = 'Rates Swap';
        if (normalized.includes('bond') || normalized.includes('treasury')) assetClass = 'Fixed Income';
        
        let counterparty = 'Pending';
        const cptyMatch = text.match(/(?:seller|counterparty|bank|dealer)[:\s]*([A-Za-z\s]+)(?:\n|$)/i);
        if (cptyMatch) {
            counterparty = cptyMatch[1].trim();
        }

        return {
            side,
            assetClass,
            symbol,
            quantity,
            price,
            counterparty
        };

    } catch (e) {
        console.error("Local Regex Parse Failed", e);
    }

    return {
        side: 'Buy',
        assetClass: 'Equity',
        symbol: 'UNKNOWN',
        quantity: 0,
        price: 0,
        counterparty: 'Pending'
    };
};

export const analyzeTradePerformance = async (trades: any[]): Promise<string> => {
    const prompt = `
        You are a Trading Performance Coach.
        Here is the execution data for recent trades:
        ${JSON.stringify(trades)}
        
        Metrics Key:
        - Slippage bps: Positive means bad (paid more), Negative is good (price improvement).
        - Arrival Px: Price when order started.
        - Exec Px: Final average price.
        
        Task:
        1. Identify any patterns of poor execution (e.g. "Consistently paying 5bps spread on Liquid Tech stocks").
        2. Suggest behavioral change (e.g. "Use Limit Orders more often", "Avoid Market Orders at the Open").
        
        Format: Concise coaching tip.
    `;
    const response = await callBackendAi({}, prompt, "tca_analysis");
    return response || "Performance analysis unavailable.";
};

export const generateInvestorUpdate = async (clientName: string, performanceData: any): Promise<string> => {
    const prompt = `
        You are an Investor Relations Officer.
        Draft a monthly performance update email for client: "${clientName}".
        
        Performance Data:
        ${JSON.stringify(performanceData)}
        
        Tone: Sophisticated, reassuring, exclusive.
        Include a brief outlook section.
    `;
    const response = await callBackendAi({}, prompt, "investor_update");
    return response || "Update generation failed.";
};

export const summarizeChatHistory = async (channelName: string, messages: any[]): Promise<string> => {
    const prompt = `
        You are a Trading Desk Assistant.
        Summarize the recent discussion in the ${channelName} channel for a Portfolio Manager who just arrived.
        
        Chat Log:
        ${JSON.stringify(messages)}
        
        Task:
        1. Identify key trade ideas or market observations discussed.
        2. Highlight any urgent risk warnings.
        3. Keep it bulleted and concise.
    `;
    const response = await callBackendAi({}, prompt, "chat_summary");
    return response || "Summary unavailable.";
};

export const processAdminCommand = async (command: string): Promise<{ action: string, details: any, confirmation: string }> => {
    const prompt = `
        You are the System Administrator AI for a Trading Platform.
        User Command: "${command}"
        
        Interpret this command into a system configuration action.
        Supported Actions: 
        - CREATE_USER (requires name, role, desk)
        - CREATE_BOOK (requires bookName, desk)
        - UPDATE_CALENDAR (requires date, currency, reason)
        - SET_LIMIT (requires entity, limitAmount)
        
        Output JSON format:
        {
            "action": "ACTION_NAME",
            "details": { ...extracted params ... },
            "confirmation": "Natural language confirmation message."
        }
    `;
    
    const response = await callBackendAi({}, prompt, "admin_command");
    if (response) {
        try {
            const jsonStr = response.match(/\{[\s\S]*\}/)?.[0] || response;
            return JSON.parse(jsonStr);
        } catch(e) { console.error(e); }
    }
    
    return {
        action: 'UNKNOWN',
        details: {},
        confirmation: "I couldn't interpret that administrative command."
    };
};

export const analyzeMacroEventImpact = async (event: string, shock: number, portfolioContext: any): Promise<string> => {
    const prompt = `
        You are a Global Macro Strategist.
        Event: ${event}
        Scenario: Outcome is ${shock > 0 ? 'Better' : 'Worse'} than consensus by ${Math.abs(shock)} Standard Deviations (${shock}σ).
        
        Portfolio Context:
        ${JSON.stringify(portfolioContext)}
        
        Task:
        1. Predict the immediate reaction of major asset classes (USD, Rates, Equities).
        2. Estimate the P&L impact on the user's specific portfolio.
        3. Recommend a hedge (e.g. "Buy 10Y Puts", "Sell USDJPY").
        
        Format: Concise, bullet points.
    `;
    const response = await callBackendAi({}, prompt, "macro_impact");
    return response || "Macro analysis unavailable.";
};

export const analyzeComplianceAlert = async (alertType: string, details: any): Promise<string> => {
    const prompt = `
        You are a Trade Surveillance Officer.
        Alert Type: "${alertType}"
        
        Details:
        ${JSON.stringify(details)}
        
        Task:
        1. Explain why this pattern is suspicious (e.g. Wash Trading indicates market manipulation).
        2. Look for mitigating factors in the details (e.g. different beneficial owners?).
        3. Recommend "Dismiss" or "Escalate".
    `;
    const response = await callBackendAi({}, prompt, "compliance_investigation");
    return response || "Compliance analysis unavailable.";
};

export const generateRebalancingOrders = async (holdings: any[], targetModel: any[]): Promise<{ orders: any[], logic: string }> => {
    const prompt = `
        You are a Portfolio Manager.
        Current Holdings: ${JSON.stringify(holdings)}
        Target Allocation Model: ${JSON.stringify(targetModel)}
        
        Task:
        1. Calculate the trades needed to align Current with Target.
        2. Generate a list of Sell orders (to raise cash/reduce overweight) and Buy orders.
        3. Provide a short rationale for the rebalance.
        
        Output JSON format:
        {
            "orders": [
                { "side": "Buy", "symbol": "AAPL", "qty": 100 },
                { "side": "Sell", "symbol": "MSFT", "qty": 50 }
            ],
            "logic": "Reducing Tech overweight to fund Fixed Income allocation."
        }
    `;
    
    const response = await callBackendAi({}, prompt, "portfolio_rebalance");
    
    if (response) {
        try {
            const jsonStr = response.match(/\{[\s\S]*\}/)?.[0] || response;
            return JSON.parse(jsonStr);
        } catch(e) { console.error(e); }
    }
    
    return {
        orders: [],
        logic: "Unable to generate rebalancing plan."
    };
};
